import { useState } from "react";
import { Button } from "@heroui/react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { TripRequestResult } from "../../interfaces/chat.interfaces";

interface ChatCompletionProps {
  tripRequest?: TripRequestResult | null;
  onNewChat: () => void;
  onViewTrip?: () => void;
}

export function ChatCompletion({
  tripRequest,
  onNewChat,
  onViewTrip,
}: ChatCompletionProps) {
  const [copied, setCopied] = useState(false);
  const hasTrip = !!tripRequest?.requestId;

  const handleCopyPassword = async () => {
    if (!tripRequest?.password) return;
    try {
      await navigator.clipboard.writeText(tripRequest.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: the password is visible for manual copying
    }
  };

  return (
    <div className="mx-2 my-3 p-4 bg-[#7bd9cc]/10 rounded-xl border border-[#7bd9cc]">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircleIcon className="text-[#159193]" fontSize="small" />
        <h3 className="font-semibold text-gray-900 text-sm">
          {hasTrip ? "Your trip is ready!" : "Trip Request Created!"}
        </h3>
      </div>

      {tripRequest?.password && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Your Trip Password</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-white border border-[#7bd9cc] rounded-lg px-3 py-2 font-mono text-sm text-gray-900 select-all">
              {tripRequest.password}
            </div>
            <Button
              size="sm"
              variant="flat"
              isIconOnly
              onPress={handleCopyPassword}
              className="min-w-8 w-8 h-8 bg-[#159193]/10 text-[#159193]"
              aria-label="Copy password"
            >
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Save this password to access your trip anytime
          </p>
        </div>
      )}

      {hasTrip ? (
        <div className="space-y-2">
          {onViewTrip && (
            <Button
              className="w-full bg-[#159193] text-white text-sm"
              size="sm"
              onPress={onViewTrip}
              endContent={<OpenInNewIcon fontSize="small" />}
            >
              View My Trip
            </Button>
          )}
          <Button
            className="w-full text-sm"
            size="sm"
            variant="light"
            onPress={onNewChat}
          >
            Start New Conversation
          </Button>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-600 mb-3">
            A travel designer will contact you within 24 hours to finalize your trip.
          </p>
          <Button
            className="w-full bg-[#159193] text-white text-sm"
            size="sm"
            onPress={onNewChat}
          >
            Start New Conversation
          </Button>
        </>
      )}
    </div>
  );
}
